import { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Loader2, Send, Paperclip } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INSTALL_OPTS = [
  { v: 'Yes', l: 'Yes — installation required' },
  { v: 'No', l: 'No — equipment only' },
  { v: 'Advise me', l: 'Advise me' },
];
const CAL_OPTS = [
  { v: 'Yes', l: 'Yes — calibration/validation required' },
  { v: 'No', l: 'No' },
  { v: 'Advise me', l: 'Advise me' },
];

function Select({ label, name, value, onChange, options, testid }) {
  return (
    <div>
      <label className="field-label" htmlFor={name}>{label}</label>
      <select id={name} name={name} value={value} onChange={onChange} className="field-input" data-testid={testid}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

export function RFQForm({ defaultNeed = '', compact = false }) {
  const location = useLocation();
  const startedAt = useRef(Date.now());
  const [f, setF] = useState({
    name: '', org: '', email: '', phone: '', need: defaultNeed, manufacturer: '',
    quantity: '', delivery_date: '', installation: 'Advise me', calibration: 'Advise me',
    solicitation: '', message: '', website: '',
  });
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState('idle');
  const [ref, setRef] = useState(null);
  const [error, setError] = useState('');

  const set = (e) => setF({ ...f, [e.target.name]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    setStatus('sending');
    setError('');
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      fd.append('elapsed_ms', String(Date.now() - startedAt.current));
      fd.append('source', location.pathname || '/');
      if (file) fd.append('attachment', file);
      const res = await fetch(`${API}/rfq`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok && !data.submitted) throw new Error(data.detail || 'Submission failed');
      setRef(data.reference || 'received');
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError('Your RFQ could not be submitted. Please try again or email info@toxglobal.com.');
    }
  }

  if (status === 'success') {
    return (
      <div className="border border-emerald-300 bg-emerald-50 p-8 sm:p-10 text-center" data-testid="rfq-success-panel">
        <CheckCircle2 className="mx-auto text-emerald-600" size={40} />
        <h3 className="display text-2xl mt-4 text-ink">RFQ received — reference {ref}</h3>
        <p className="mt-3 text-slate-600 max-w-md mx-auto">
          A confirmation email is on its way to {f.email}. For urgent requirements, call 877.225.0316 and reference {ref}.
        </p>
        <button
          className="btn-ghost mt-6"
          onClick={() => { setStatus('idle'); setF({ ...f, need: defaultNeed, message: '', solicitation: '', quantity: '' }); setFile(null); }}
          data-testid="rfq-reset-button"
        >
          Submit another RFQ
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="relative" data-testid="rfq-form" noValidate>
      <input
        type="text" name="website" value={f.website} onChange={set}
        className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true"
        data-testid="rfq-honeypot"
      />
      <div className="bg-white border border-slate-200 p-6 sm:p-10">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-8">
          <h3 className="display text-2xl text-ink">Request a Quote / Send an RFQ</h3>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 bg-slate-100 px-2.5 py-1">GPC · PO · ACH accepted</span>
        </div>

        <div className={`grid gap-5 ${compact ? 'sm:grid-cols-2' : 'sm:grid-cols-2'}`}>
          <div>
            <label className="field-label" htmlFor="rfq-name">Name *</label>
            <input id="rfq-name" name="name" required value={f.name} onChange={set} className="field-input" placeholder="Jane Doe" data-testid="rfq-name-input" />
          </div>
          <div>
            <label className="field-label" htmlFor="rfq-org">Organization / Agency *</label>
            <input id="rfq-org" name="org" required value={f.org} onChange={set} className="field-input" placeholder="Agency, laboratory, or company" data-testid="rfq-org-input" />
          </div>
          <div>
            <label className="field-label" htmlFor="rfq-email">Email *</label>
            <input id="rfq-email" name="email" type="email" required value={f.email} onChange={set} className="field-input" placeholder="name@agency.gov" data-testid="rfq-email-input" />
          </div>
          <div>
            <label className="field-label" htmlFor="rfq-phone">Phone</label>
            <input id="rfq-phone" name="phone" type="tel" value={f.phone} onChange={set} className="field-input" placeholder="Optional" data-testid="rfq-phone-input" />
          </div>
          <div className="sm:col-span-2">
            <label className="field-label" htmlFor="rfq-need">Equipment or Service Needed *</label>
            <input id="rfq-need" name="need" required value={f.need} onChange={set} className="field-input" placeholder="e.g. GC/MS system with headspace sampler" data-testid="rfq-need-input" />
          </div>
          <div>
            <label className="field-label" htmlFor="rfq-manufacturer">Manufacturer / Model (if known)</label>
            <input id="rfq-manufacturer" name="manufacturer" value={f.manufacturer} onChange={set} className="field-input" placeholder="Optional" data-testid="rfq-manufacturer-input" />
          </div>
          <div>
            <label className="field-label" htmlFor="rfq-quantity">Quantity</label>
            <input id="rfq-quantity" name="quantity" value={f.quantity} onChange={set} className="field-input" placeholder="e.g. 2" data-testid="rfq-quantity-input" />
          </div>
          <div>
            <label className="field-label" htmlFor="rfq-delivery">Required delivery date</label>
            <input id="rfq-delivery" name="delivery_date" type="date" value={f.delivery_date} onChange={set} className="field-input" data-testid="rfq-delivery-input" />
          </div>
          <div>
            <label className="field-label" htmlFor="rfq-solicitation">Solicitation / RFQ number</label>
            <input id="rfq-solicitation" name="solicitation" value={f.solicitation} onChange={set} className="field-input" placeholder="Optional" data-testid="rfq-solicitation-input" />
          </div>
          <Select label="Installation required?" name="installation" value={f.installation} onChange={set} options={INSTALL_OPTS} testid="rfq-installation-select" />
          <Select label="Calibration / validation required?" name="calibration" value={f.calibration} onChange={set} options={CAL_OPTS} testid="rfq-calibration-select" />
          <div className="sm:col-span-2">
            <label className="field-label" htmlFor="rfq-message">Message</label>
            <textarea id="rfq-message" name="message" rows={4} value={f.message} onChange={set} className="field-input" placeholder="Describe the requirement, application, or deadline" data-testid="rfq-message-input" />
          </div>
          <div className="sm:col-span-2">
            <label className="field-label" htmlFor="rfq-file">Attach specs / SOW (PDF, DOC, images — max 10 MB)</label>
            <label
              htmlFor="rfq-file"
              className="flex items-center gap-3 border border-dashed border-slate-300 px-4 py-3.5 text-sm text-slate-500 hover:border-signal hover:text-signal transition-colors cursor-pointer"
              data-testid="rfq-file-dropzone"
            >
              <Paperclip size={16} />
              <span>{file ? file.name : 'Choose a file'}</span>
            </label>
            <input
              id="rfq-file" type="file" className="sr-only"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg"
              onChange={(e) => setFile(e.target.files[0] || null)}
              data-testid="rfq-file-input"
            />
          </div>
        </div>

        {status === 'error' && (
          <div className="mt-5 flex items-start gap-2 border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3" data-testid="rfq-error-alert">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {error}
          </div>
        )}

        <button type="submit" disabled={status === 'sending'} className="btn-primary w-full sm:w-auto mt-7 disabled:opacity-60" data-testid="rfq-submit-button">
          {status === 'sending' ? (
            <><Loader2 size={16} className="animate-spin" /> Submitting…</>
          ) : (
            <><Send size={15} /> Submit RFQ</>
          )}
        </button>
        <p className="mt-4 text-xs text-slate-400">
          Submissions are answered by ToxGlobal Analytics in order of receipt. Your information is used only to respond to this
          requirement. This form is protected against automated spam.
        </p>
      </div>
    </form>
  );
}
